/**
 * Per-vehicle deep-dive page (PR 5.5 of dashboard standards).
 *
 * Reached by clicking a row in:
 *   * the Top Offenders panel on the Fraud Hub  (`/app/vehicle/:assetToken`)
 *   * the Top Assets table on the Overview page (same)
 *
 * Layout (top to bottom):
 *   1. Breadcrumb + header (registration, make/model, short token).
 *   2. <TimePeriodSelector defaultPeriod="M" /> — URL-backed via `?period=`.
 *   3. <VehicleDnaPanel /> — OEM / peer / own-baseline / actual consumption
 *      benchmarks plus operating cost. Self-hiding when the vehicle has no
 *      DNA row, so everything below is unaffected by a 404.
 *   4. Alert-count badge ("23 alerts across 5 stations").
 *   5. <EntityLocationMap /> with markers for the top N alert stations.
 *   6. Compact <Table> of those stations (rank, name, count, last alert).
 *   7. CTA back to Fraud Center pre-filtered to this asset.
 *
 * The page only fetches when the URL has a non-placeholder asset token,
 * so React Router's transitional render doesn't fire spurious 404s.
 */

import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, MapPin } from 'lucide-react';

import EntityLocationMap, {
  type EntityLocationMarker,
} from '@/components/shared/EntityLocationMap';
import { TimePeriodSelector } from '@/components/shared/TimePeriodSelector';
import { VehicleDnaPanel } from '@/components/vehicle/VehicleDnaPanel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useVehicleAlertStations } from '@/api/hooks/useVehicleDetail';
import { useVehicleProfile } from '@/api/hooks/useVehicleProfile';
import { formatAlertTime, shortToken } from '@/lib/format';
import { parsePeriod } from '@/lib/timePeriod';

export function VehicleDetail(): JSX.Element {
  const { assetToken } = useParams<{ assetToken: string }>();
  const [searchParams] = useSearchParams();
  const period = parsePeriod(searchParams.get('period'), 'M');

  const profileQuery = useVehicleProfile(assetToken);
  const stationsQuery = useVehicleAlertStations({
    assetToken,
    period,
    limit: 5,
  });

  // Stable marker reference — the EntityLocationMap effect bails when
  // the dependency identity is unchanged, so we memoise on the
  // stations array so unrelated re-renders don't trigger marker churn.
  const markers = useMemo<EntityLocationMarker[]>(
    () =>
      (stationsQuery.data?.stations ?? []).map((s) => ({
        lat: s.lat,
        lon: s.lon,
        label: s.station_name,
        count: s.alert_count,
      })),
    [stationsQuery.data?.stations],
  );

  const profile = profileQuery.data;
  const stationsResp = stationsQuery.data;
  const stations = stationsResp?.stations ?? [];

  const makeModel = profile
    ? [profile.make, profile.model].filter(Boolean).join(' ') || null
    : null;

  return (
    <div className="space-y-6 p-6">
      {/* Breadcrumb + back link */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Button asChild size="sm" variant="ghost" className="h-7 px-2">
          <Link to="/fraud-hub">
            <ChevronLeft className="mr-1 h-4 w-4" />
            Fraud Hub
          </Link>
        </Button>
        <span>/</span>
        <span className="font-medium text-foreground">Vehicle</span>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          {profileQuery.isLoading ? (
            <Skeleton className="h-8 w-48" />
          ) : profileQuery.isError || !profile ? (
            <div>
              <h1 className="text-2xl font-bold">Vehicle</h1>
              <p className="mt-1 font-mono text-xs text-muted-foreground">
                {shortToken(assetToken ?? null)}
              </p>
              {profileQuery.isError && (
                <p className="mt-1 text-sm text-destructive">
                  Failed to load profile: {(profileQuery.error as Error).message}
                </p>
              )}
            </div>
          ) : (
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                {profile.registration}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {makeModel ?? <span className="italic">Make / model not on file</span>}
                {profile.manufacture_year != null && makeModel
                  ? ` · ${profile.manufacture_year}`
                  : ''}
                <span className="ml-2 font-mono text-xs">
                  · {shortToken(profile.asset_token)}
                </span>
              </p>
            </div>
          )}
        </div>
        <TimePeriodSelector defaultPeriod="M" />
      </div>

      {/* Vehicle DNA — four consumption benchmarks + operating cost. Keyed on
          registration, so it stays idle until the token-addressed profile
          resolves one, and renders nothing at all when the vehicle has no
          DNA row (404) — the map/stations content below must stand alone. */}
      <VehicleDnaPanel registration={profile?.registration} />

      {/* Alert-stations card: badge + map + table */}
      <Card>
        <CardHeader className="flex flex-col gap-2 pb-3 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="h-4 w-4" />
              Top alert stations
              {stationsResp?.period_label
                ? ` — ${stationsResp.period_label}`
                : ''}
            </CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              The 5 stations where this vehicle has logged the most fraud
              alerts in the period — not where it transacts most.
            </p>
          </div>
          {stationsResp && (
            <Badge variant="secondary" className="self-start text-xs">
              {stationsResp.total_alerts.toLocaleString('en-ZA')}{' '}
              {stationsResp.total_alerts === 1 ? 'alert' : 'alerts'} · {stations.length}{' '}
              {stations.length === 1 ? 'station' : 'stations'}
            </Badge>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {stationsQuery.isLoading ? (
            <Skeleton className="h-[240px] w-full" />
          ) : stationsQuery.isError ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              Failed to load alert stations:{' '}
              {(stationsQuery.error as Error).message}
            </div>
          ) : (
            <EntityLocationMap markers={markers} height="240px" />
          )}

          {!stationsQuery.isLoading && !stationsQuery.isError && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Station</TableHead>
                  <TableHead className="text-right">Alerts</TableHead>
                  <TableHead>Last alert</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stations.map((s, idx) => (
                  <TableRow key={s.station_id}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {idx + 1}
                    </TableCell>
                    <TableCell className="text-sm">{s.station_name}</TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {s.alert_count.toLocaleString('en-ZA')}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatAlertTime(s.last_alert_at)}
                    </TableCell>
                  </TableRow>
                ))}
                {stations.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-center text-sm text-muted-foreground"
                    >
                      No alert stations in this period.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* CTA back to Fraud Center pre-filtered */}
      {assetToken && (
        <div className="flex justify-end">
          <Button asChild variant="outline" size="sm">
            <Link to={`/fraud?asset_token=${encodeURIComponent(assetToken)}`}>
              View all alerts for this vehicle in Fraud Center →
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
}

export default VehicleDetail;
